import { useEffect, type ReactNode } from 'react'
import { useAuth } from '../shell/AuthContext'
import { Spinner } from '../shell/Spinner'

/**
 * Hold the application until there is a Keycloak session, redirecting to sign in when there is not.
 *
 * This replaces the Blazor client's entire session-expiry story, which was: any 401 that was not
 * from /auth/* cleared local state and navigated to `/login?reason=expired`. Here expiry is the
 * OIDC library's business — it renews silently — and this component only handles the case where
 * renewal has genuinely failed.
 *
 * `error` is treated exactly like "not authenticated" on purpose. When silent renew fails,
 * react-oidc-context sets an error and leaves `isAuthenticated` true against a session Keycloak
 * has already ended; showing the app in that state produces a portal where every request 401s.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, error, login } = useAuth()

  const needsSignIn = !isLoading && (!isAuthenticated || !!error)

  useEffect(() => {
    if (needsSignIn) login()
  }, [needsSignIn, login])

  if (isLoading || needsSignIn) return <Spinner full />

  return <>{children}</>
}
